import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { disputesApi, coachApi, notificationsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  TrendingUp, TrendingDown, FileText, CheckCircle, AlertCircle, ArrowRight, 
  MessageCircle, Zap, Plus, Target, Calendar, Minus, BarChart3, Flag, Trophy, X,
  Bell, Flame, Star
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';
import { toast } from 'sonner';

export default function DashboardOverview() {
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [scoreStats, setScoreStats] = useState(null);
  const [scoreGoal, setScoreGoal] = useState(null);
  const [goalProgress, setGoalProgress] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [addScoreOpen, setAddScoreOpen] = useState(false);
  const [setGoalOpen, setSetGoalOpen] = useState(false);
  const [newScore, setNewScore] = useState({ score: '', bureau: 'Experian', notes: '' });
  const [newGoal, setNewGoal] = useState({ targetScore: '', targetDate: '', notes: '' });
  const [savingScore, setSavingScore] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);

  useEffect(function loadDashboardData() {
    async function fetchData() {
      try {
        const disputesData = await disputesApi.getAll();
        setDisputes(disputesData.disputes || []);
      } catch (err) {
        console.error('Failed to fetch disputes:', err);
      }
      try {
        const insightsData = await coachApi.getInsights();
        setInsights(insightsData);
      } catch (err) {
        console.error('Failed to fetch insights:', err);
      }
      try {
        const scoreData = await disputesApi.getCreditScoreHistory();
        setScoreHistory(scoreData.scores || []);
        setScoreStats(scoreData.stats || null);
        setScoreGoal(scoreData.goal || null);
      } catch (err) {
        console.error('Failed to fetch credit scores:', err);
      }
      try {
        const goalData = await disputesApi.getScoreGoal();
        if (goalData.goal) {
          setScoreGoal(goalData.goal);
          setGoalProgress(goalData.progress);
        }
      } catch (err) {
        console.error('Failed to fetch goal:', err);
      }
      try {
        const notifData = await notificationsApi.getAll();
        // Filter for goal-related unread notifications
        const goalNotifs = (notifData.notifications || []).filter(function(n) {
          return !n.read && (n.type === 'goal_reminder' || n.type === 'goal_achieved');
        });
        setNotifications(goalNotifs);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  const handleNavigate = useCallback(function(path) {
    navigate(path);
  }, [navigate]);

  const handleDismissNotification = useCallback(async function(notificationId) {
    try {
      await notificationsApi.markRead(notificationId);
      setNotifications(function(prev) {
        return prev.filter(function(n) { return n.notification_id !== notificationId; });
      });
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  }, []);

  const handleAddScore = useCallback(async function() {
    if (!newScore.score || newScore.score < 300 || newScore.score > 850) {
      toast.error('Please enter a valid score between 300 and 850');
      return;
    }
    
    setSavingScore(true);
    try {
      await disputesApi.addCreditScore(
        parseInt(newScore.score), 
        newScore.bureau, 
        newScore.notes || null
      );
      toast.success('Credit score recorded!');
      setAddScoreOpen(false);
      setNewScore({ score: '', bureau: 'Experian', notes: '' });
      
      // Refresh score data
      const scoreData = await disputesApi.getCreditScoreHistory();
      setScoreHistory(scoreData.scores || []);
      setScoreStats(scoreData.stats || null);
      
      // Refresh goal progress
      const goalData = await disputesApi.getScoreGoal();
      if (goalData.progress) {
        setGoalProgress(goalData.progress);
      }
    } catch (err) {
      toast.error('Failed to save credit score');
    } finally {
      setSavingScore(false);
    }
  }, [newScore]);

  const handleSetGoal = useCallback(async function() {
    if (!newGoal.targetScore || newGoal.targetScore < 300 || newGoal.targetScore > 850) {
      toast.error('Please enter a valid target score between 300 and 850');
      return;
    }
    
    setSavingGoal(true);
    try {
      await disputesApi.setScoreGoal(
        parseInt(newGoal.targetScore),
        newGoal.targetDate || null,
        newGoal.notes || null
      );
      toast.success('Score goal set! 🎯');
      setSetGoalOpen(false);
      setNewGoal({ targetScore: '', targetDate: '', notes: '' });
      
      // Refresh goal data
      const goalData = await disputesApi.getScoreGoal();
      setScoreGoal(goalData.goal);
      setGoalProgress(goalData.progress);
    } catch (err) {
      toast.error('Failed to set goal');
    } finally {
      setSavingGoal(false);
    }
  }, [newGoal]);

  const handleDeleteGoal = useCallback(async function() {
    try {
      await disputesApi.deleteScoreGoal();
      toast.success('Goal removed');
      setScoreGoal(null);
      setGoalProgress(null);
    } catch (err) {
      toast.error('Failed to delete goal');
    }
  }, []);

  function getStatusColor(status) {
    if (status === 'sent') return 'bg-blue-100 text-blue-700';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (status === 'in_review') return 'bg-purple-100 text-purple-700';
    if (status === 'resolved') return 'bg-green-100 text-green-700';
    return 'bg-slate-100 text-slate-700';
  }

  function getScoreColor(score) {
    if (score >= 750) return 'text-green-600';
    if (score >= 700) return 'text-teal-600';
    if (score >= 650) return 'text-amber-600';
    if (score >= 600) return 'text-orange-600';
    return 'text-red-600';
  }

  function getScoreLabel(score) {
    if (score >= 750) return 'Excellent';
    if (score >= 700) return 'Good';
    if (score >= 650) return 'Fair';
    if (score >= 600) return 'Poor';
    return 'Very Poor';
  }

  function getScoreBgColor(score) {
    if (score >= 750) return 'bg-green-100';
    if (score >= 700) return 'bg-teal-100';
    if (score >= 650) return 'bg-amber-100';
    if (score >= 600) return 'bg-orange-100';
    return 'bg-red-100';
  }

  function countActiveDisputes() {
    let count = 0;
    for (let i = 0; i < disputes.length; i++) {
      const status = disputes[i].status;
      if (status === 'sent' || status === 'pending' || status === 'in_review') {
        count++;
      }
    }
    return count;
  }

  function countResolvedDisputes() {
    let count = 0;
    for (let i = 0; i < disputes.length; i++) {
      if (disputes[i].status === 'resolved') {
        count++;
      }
    }
    return count;
  }

  function getSuccessRate() {
    if (insights && insights.success_rate !== undefined) {
      return insights.success_rate.toFixed(0);
    }
    return '0';
  }

  function formatChartData() {
    if (!scoreHistory || scoreHistory.length === 0) return [];
    
    // Reverse to show oldest first, limit to last 12 entries
    const sorted = [...scoreHistory].reverse().slice(-12);
    
    return sorted.map(function(entry) {
      const date = new Date(entry.recorded_at);
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        score: entry.score,
        bureau: entry.bureau,
        fullDate: date.toLocaleDateString()
      };
    });
  }

  function renderStatCard(title, value, IconComponent, bgColor, iconColor) {
    return (
      <Card className="border-slate-100 shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">{title}</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            </div>
            <div className={'w-12 h-12 rounded-xl flex items-center justify-center ' + bgColor}>
              <IconComponent className={'h-6 w-6 ' + iconColor} />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderQuickAction(iconComponent, label, path, testId) {
    const IconComp = iconComponent;
    return (
      <Button 
        variant="outline" 
        className="w-full justify-start h-12"
        onClick={function() { handleNavigate(path); }}
        data-testid={testId}
      >
        <IconComp className="mr-3 h-5 w-5 text-slate-500" />
        {label}
        <ArrowRight className="ml-auto h-4 w-4 text-slate-400" />
      </Button>
    );
  }

  function renderDisputeItem(dispute) {
    const itemCount = dispute.items ? dispute.items.length : 0;
    const dateStr = new Date(dispute.created_at).toLocaleDateString();
    
    return (
      <div 
        key={dispute.dispute_id}
        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
        onClick={function() { handleNavigate('/dashboard/disputes/' + dispute.dispute_id); }}
        data-testid={'dispute-item-' + dispute.dispute_id}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
            <FileText className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <p className="font-medium text-slate-900">{dispute.bureau}</p>
            <p className="text-sm text-slate-500">
              {itemCount} items • {dateStr}
            </p>
          </div>
        </div>
        <Badge className={getStatusColor(dispute.status)}>
          {dispute.status}
        </Badge>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/4" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-32 bg-slate-200 rounded-xl" />
          <div className="h-32 bg-slate-200 rounded-xl" />
          <div className="h-32 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  const userName = user ? user.first_name : 'User';
  const isAuthorized = user ? user.is_authorized : false;
  const activeCount = countActiveDisputes();
  const resolvedCount = countResolvedDisputes();
  const successRate = getSuccessRate();
  const recentDisputes = disputes.slice(0, 5);
  const chartData = formatChartData();
  const currentScore = scoreStats?.current;
  const scoreChange = scoreStats?.change_30d;

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Welcome back, {userName}!
        </h1>
        <p className="text-slate-600 mt-1">Here's your credit improvement progress.</p>
      </div>

      {/* Goal Reminder Notifications */}
      {notifications.length > 0 && (
        <div className="space-y-3" data-testid="goal-notifications">
          {notifications.map(function(notif) {
            const isAchieved = notif.type === 'goal_achieved';
            return (
              <Card 
                key={notif.notification_id}
                className={`border-2 ${isAchieved ? 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50'}`}
                data-testid={'notification-' + notif.notification_id}
              >
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isAchieved ? 'bg-green-100' : 'bg-amber-100'}`}>
                      {isAchieved ? (
                        <Trophy className="h-6 w-6 text-green-600" />
                      ) : notif.title.includes('Almost') || notif.title.includes('So Close') ? (
                        <Flame className="h-6 w-6 text-amber-600" />
                      ) : (
                        <Star className="h-6 w-6 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className={`font-semibold ${isAchieved ? 'text-green-800' : 'text-amber-800'}`}>
                        {notif.title}
                      </p>
                      <p className={`text-sm ${isAchieved ? 'text-green-700' : 'text-amber-700'}`}>
                        {notif.message}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={`${isAchieved ? 'text-green-600 hover:bg-green-100' : 'text-amber-600 hover:bg-amber-100'}`}
                    onClick={function() { handleDismissNotification(notif.notification_id); }}
                    data-testid={'dismiss-notif-' + notif.notification_id}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!isAuthorized && (
        <Card className="border-amber-200 bg-amber-50" data-testid="auth-alert">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <span className="text-amber-800">Complete your authorization to enable automatic disputes.</span>
            </div>
            <Button 
              onClick={function() { handleNavigate('/onboarding'); }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="complete-auth-btn"
            >
              Complete Setup
            </Button>
          </CardContent>
        </Card>
      )}

      {!isPremium && isAuthorized && (
        <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-teal-100/50" data-testid="upgrade-banner">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-teal-600" />
              <span className="text-teal-800 font-medium">Upgrade to Premium for automatic dispute sending</span>
            </div>
            <Button 
              onClick={function() { handleNavigate('/pricing'); }}
              className="btn-teal"
              data-testid="upgrade-btn"
            >
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Credit Score Hero Card */}
      <Card className="border-slate-100 shadow-sm overflow-hidden" data-testid="credit-score-card">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className={`w-24 h-24 md:w-28 md:h-28 rounded-2xl flex flex-col items-center justify-center ${currentScore ? getScoreBgColor(currentScore) : 'bg-slate-200'}`}>
                {currentScore ? (
                  <>
                    <span className={`text-3xl md:text-4xl font-bold ${getScoreColor(currentScore)}`}>{currentScore}</span>
                    <span className={`text-xs font-medium ${getScoreColor(currentScore)}`}>{getScoreLabel(currentScore)}</span>
                  </>
                ) : (
                  <>
                    <Target className="h-8 w-8 text-slate-400" />
                    <span className="text-xs text-slate-500 mt-1">No Score</span>
                  </>
                )}
              </div>
              <div>
                <h2 className="text-white text-lg md:text-xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                  Your Credit Score
                </h2>
                {scoreChange !== null && scoreChange !== undefined ? (
                  <div className="flex items-center gap-2 mt-2">
                    {scoreChange > 0 ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        +{scoreChange} pts (30 days)
                      </Badge>
                    ) : scoreChange < 0 ? (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <TrendingDown className="h-3 w-3 mr-1" />
                        {scoreChange} pts (30 days)
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
                        <Minus className="h-3 w-3 mr-1" />
                        No change (30 days)
                      </Badge>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm mt-2">Track your score over time</p>
                )}
                {scoreStats && scoreStats.total_entries > 0 && (
                  <p className="text-slate-500 text-xs mt-2">
                    {scoreStats.total_entries} entries recorded • Range: {scoreStats.lowest} - {scoreStats.highest}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Set Goal Button */}
              <Dialog open={setGoalOpen} onOpenChange={setSetGoalOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 gap-2" data-testid="set-goal-btn">
                    <Flag className="h-4 w-4" />
                    {scoreGoal ? 'Update Goal' : 'Set Goal'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="set-goal-dialog">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope' }}>Set Your Credit Score Goal</DialogTitle>
                    <DialogDescription>
                      Set a target score to track your progress and stay motivated.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Target Score</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 750"
                        min={300}
                        max={850}
                        value={newGoal.targetScore}
                        onChange={function(e) { setNewGoal(function(prev) { return { ...prev, targetScore: e.target.value }; }); }}
                        data-testid="goal-score-input"
                      />
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[700, 720, 750, 780, 800].map(function(score) {
                          return (
                            <Button 
                              key={score}
                              type="button"
                              variant="outline" 
                              size="sm"
                              onClick={function() { setNewGoal(function(prev) { return { ...prev, targetScore: score.toString() }; }); }}
                              className={newGoal.targetScore === score.toString() ? 'border-teal-500 bg-teal-50' : ''}
                            >
                              {score}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Target Date (optional)</Label>
                      <Input
                        type="date"
                        value={newGoal.targetDate}
                        onChange={function(e) { setNewGoal(function(prev) { return { ...prev, targetDate: e.target.value }; }); }}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="goal-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notes (optional)</Label>
                      <Input
                        placeholder="e.g., Before mortgage application"
                        value={newGoal.notes}
                        onChange={function(e) { setNewGoal(function(prev) { return { ...prev, notes: e.target.value }; }); }}
                        data-testid="goal-notes-input"
                      />
                    </div>
                    <Button 
                      className="w-full btn-teal"
                      onClick={handleSetGoal}
                      disabled={savingGoal || !newGoal.targetScore}
                      data-testid="save-goal-btn"
                    >
                      {savingGoal ? 'Saving...' : 'Set Goal'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Log Score Button */}
              <Dialog open={addScoreOpen} onOpenChange={setAddScoreOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600 text-white gap-2" data-testid="add-score-btn">
                    <Plus className="h-4 w-4" />
                    Log Score
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="add-score-dialog">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope' }}>Log Your Credit Score</DialogTitle>
                    <DialogDescription>
                      Enter your current credit score from any bureau to track your progress.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Credit Score</Label>
                      <Input
                        type="number"
                        placeholder="Enter score (300-850)"
                        min={300}
                        max={850}
                        value={newScore.score}
                        onChange={function(e) { setNewScore(function(prev) { return { ...prev, score: e.target.value }; }); }}
                        data-testid="score-input"
                      />
                      <p className="text-xs text-slate-500">Scores range from 300 (poor) to 850 (excellent)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Credit Bureau</Label>
                      <Select 
                        value={newScore.bureau}
                        onValueChange={function(value) { setNewScore(function(prev) { return { ...prev, bureau: value }; }); }}
                      >
                        <SelectTrigger data-testid="bureau-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Experian">Experian</SelectItem>
                          <SelectItem value="Equifax">Equifax</SelectItem>
                          <SelectItem value="TransUnion">TransUnion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes (optional)</Label>
                      <Input
                        placeholder="e.g., From Credit Karma"
                        value={newScore.notes}
                        onChange={function(e) { setNewScore(function(prev) { return { ...prev, notes: e.target.value }; }); }}
                        data-testid="score-notes"
                      />
                    </div>
                    <Button 
                      className="w-full btn-teal"
                      onClick={handleAddScore}
                      disabled={savingScore || !newScore.score}
                      data-testid="save-score-btn"
                    >
                      {savingScore ? 'Saving...' : 'Save Score'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Goal Progress Section */}
          {scoreGoal && currentScore && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-teal-400" />
                  <span className="text-white text-sm font-medium">Goal: {scoreGoal.target_score}</span>
                  {goalProgress?.achieved && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <Trophy className="h-3 w-3 mr-1" />
                      Achieved!
                    </Badge>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-slate-400 hover:text-white h-6 w-6 p-0"
                  onClick={handleDeleteGoal}
                  data-testid="delete-goal-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {goalProgress && !goalProgress.achieved && (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <Progress value={goalProgress.percentage} className="flex-1 h-2 bg-white/20" />
                    <span className="text-white text-sm font-medium">{goalProgress.percentage}%</span>
                  </div>
                  <p className="text-teal-300 text-sm">{goalProgress.message}</p>
                </>
              )}
              
              {goalProgress?.achieved && (
                <p className="text-green-400 text-sm">{goalProgress.message}</p>
              )}
            </div>
          )}
        </div>
        
        {/* Score Chart */}
        <CardContent className="pt-6">
          {chartData.length > 1 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0D9488" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0D9488" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis domain={['dataMin - 20', 'dataMax + 20']} stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    formatter={function(value, name, props) {
                      return [value, 'Score'];
                    }}
                    labelFormatter={function(label, payload) {
                      if (payload && payload[0]) {
                        return payload[0].payload.fullDate + ' (' + payload[0].payload.bureau + ')';
                      }
                      return label;
                    }}
                  />
                  {scoreGoal && (
                    <ReferenceLine y={scoreGoal.target_score} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Goal', fill: '#f59e0b', fontSize: 10 }} />
                  )}
                  <ReferenceLine y={700} stroke="#22c55e" strokeDasharray="3 3" label={{ value: 'Good', fill: '#22c55e', fontSize: 10 }} />
                  <Area 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#0D9488" 
                    strokeWidth={2}
                    fill="url(#scoreGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : chartData.length === 1 ? (
            <div className="text-center py-8 text-slate-500">
              <BarChart3 className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">One score recorded</p>
              <p className="text-sm mt-1">Add more scores to see your progress chart</p>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Start tracking your credit score</p>
              <p className="text-sm mt-1">Click "Log Score" to record your first entry</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {renderStatCard('Active Disputes', activeCount, FileText, 'bg-blue-100', 'text-blue-600')}
        {renderStatCard('Resolved', resolvedCount, CheckCircle, 'bg-green-100', 'text-green-600')}
        {renderStatCard('Success Rate', successRate + '%', TrendingUp, 'bg-teal-100', 'text-teal-600')}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-100 shadow-sm" data-testid="recent-disputes-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Recent Disputes</CardTitle>
              <CardDescription>Your latest dispute activity</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={function() { handleNavigate('/dashboard/disputes'); }}
              data-testid="view-all-disputes-btn"
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {disputes.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No disputes yet. Add credit items to get started.</p>
                <Button 
                  className="mt-4 btn-teal"
                  onClick={function() { handleNavigate('/dashboard/disputes'); }}
                  data-testid="start-dispute-btn"
                >
                  Start Your First Dispute
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentDisputes.map(renderDisputeItem)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-100 shadow-sm" data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {renderQuickAction(FileText, 'View All Disputes', '/dashboard/disputes', 'view-disputes-btn')}
            {renderQuickAction(MessageCircle, 'Chat with AI Coach', '/dashboard/coach', 'chat-coach-btn')}
            {renderQuickAction(Plus, 'Upload Documents', '/dashboard/documents', 'upload-docs-btn')}
          </CardContent>
        </Card>
      </div>

      {/* Credit Improvement Plan */}
      <Card className="border-slate-100 shadow-sm bg-gradient-to-r from-slate-50 to-teal-50/30" data-testid="credit-improvement-plan">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Credit Improvement Plan</CardTitle>
          <CardDescription>Follow these steps to boost your credit score</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={function() { handleNavigate('/dashboard/disputes'); }}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all text-left"
              data-testid="plan-step-1"
            >
              <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-sm">1</div>
              <div>
                <p className="font-medium text-slate-800">Review Credit</p>
                <p className="text-xs text-slate-500">Check your report</p>
              </div>
            </button>
            <button 
              onClick={function() { handleNavigate('/dashboard/disputes'); }}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all text-left"
              data-testid="plan-step-2"
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">2</div>
              <div>
                <p className="font-medium text-slate-800">Dispute Errors</p>
                <p className="text-xs text-slate-500">Fix inaccuracies</p>
              </div>
            </button>
            <button 
              onClick={function() { handleNavigate('/dashboard/credit-marketplace'); }}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all text-left"
              data-testid="plan-step-3"
            >
              <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm">3</div>
              <div>
                <p className="font-medium text-slate-800">Build Credit</p>
                <p className="text-xs text-slate-500">Add positive accounts</p>
              </div>
            </button>
            <button 
              onClick={function() { handleNavigate('/dashboard/financial-marketplace'); }}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all text-left"
              data-testid="plan-step-4"
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm">4</div>
              <div>
                <p className="font-medium text-slate-800">Grow Savings</p>
                <p className="text-xs text-slate-500">Start investing</p>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
